import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { ManifestIntent } from '../api/authoring'
import { applicableIntents, formableIntents, inFieldOrder, intentWrites, structuralIntents, typeOf } from '../model/manifest'
import type { StructuralRole } from '../model/schema'
import { IntentForm } from './IntentForm'
import type { EditorProps } from './types'

/**
 * The editor for a type nobody wrote an editor for — which is now most of
 * them, and the point.
 *
 * **This is what the field schemas bought.** Before them, a type absent
 * from the editor map was placeable and unreadable, and the fallback that
 * used to live here said so out loud. Now the manifest carries what a
 * document holds and what each intent may send, both reflected off the Go
 * structs, so a form for a type this client has never heard of is
 * derived rather than written: the tenth component type is editable the
 * day it is registered.
 *
 * The split between a form and a placement control is read from the
 * payload — see `formableIntents` and `structuralIntents` — never from a
 * list here. A list would be a second declaration of something the schema
 * already says, and it would be missing that tenth type.
 */
export function GenericEditor({ fields, references, manifest, typeName, register, omit, busy, send }: EditorProps) {
  const declared = typeOf(manifest, typeName)
  const offered = formableIntents(manifest, typeName, fields).filter((intent) => !omit?.has(intent.name))
  // To a site's owner the forms read top to bottom as the type lists its
  // fields, not as the manifest sorts intent names.
  const formable = register === 'content' ? inFieldOrder(manifest, typeName, offered) : offered

  // The remainder, by difference rather than by rule: what this client
  // could not draw a complete form for is exactly what it did not put in
  // `formable`, so the two lists cannot disagree about an intent.
  //
  // It is split again, because "names a placement" and "this client has
  // no control for it" are different things to tell an author. The first
  // is the model working as designed — the edit belongs on the tree. The
  // second is a gap: `form.fields_reordered` orders a Form's own inputs,
  // which are named rather than linked, so it trips no marker and is
  // still not a form. Calling that structural would be blaming the model
  // for a control nobody has written.
  const drawn = new Set(formable.map((intent) => intent.name))
  const remainder = applicableIntents(manifest, typeName, fields).filter(
    (intent) => !drawn.has(intent.name) && !omit?.has(intent.name),
  )
  const roles = new Map(structuralIntents(manifest, typeName).map((intent) => [intent.name, intent.role]))
  const structural = remainder.filter((intent) => roles.has(intent.name))
  const undrawable = remainder.filter((intent) => !roles.has(intent.name))

  return (
    <Stack spacing={3}>
      {formable.map((intent) => (
        <IntentForm
          key={intent.name}
          intent={intent}
          fields={fields}
          // One field says what it is in its own label, so a heading over
          // it repeats it, in the intent's words: "Headed" over "Header".
          heading={register === 'content' && intentWrites(intent).length === 1 ? false : undefined}
          holder={typeName}
          references={references}
          showName={register === 'build'}
          busy={busy}
          send={send}
        />
      ))}

      {/* An empty form is a real thing to say in build mode and a dead
          end in content mode, where the edits it is pointing at are made
          on the tree the author is already looking at. */}
      {formable.length === 0 && register === 'build' && (
        <>
          <Alert severity="info">
            {declared ? (
              <>
                No form can be derived for <strong>{typeName}</strong>. Every intent it declares names part of the
                component tree, so its edits are placements — made on the tree, not in a form.
              </>
            ) : (
              <>
                This build&rsquo;s manifest does not declare <strong>{typeName}</strong>, so there is nothing to derive
                a form from. Its fields are shown as stored.
              </>
            )}
          </Alert>

          {/* Read-only, and deliberately not a JSON textarea. Editing a
              document and posting it back is exactly the whole-document
              write the authoring surface refuses to accept, and building
              one here would let an author do through a back door what
              every other control in this client is shaped to prevent. */}
          <Stack spacing={0.5}>
            <Typography variant="h3">Fields</Typography>
            <Typography
              component="pre"
              variant="body2"
              sx={{
                fontFamily: 'ui-monospace, monospace',
                fontSize: 12,
                bgcolor: 'action.hover',
                p: 1.5,
                borderRadius: 1,
                overflowX: 'auto',
              }}
            >
              {JSON.stringify(fields, null, 2)}
            </Typography>
          </Stack>
        </>
      )}

      {formable.length === 0 && register === 'content' && (
        <Alert severity="info">
          There is nothing to fill in here. What this holds is arranged on the page rather than typed into a form.
        </Alert>
      )}

      {/* Named rather than hidden — in build mode. An author who can see
          that an edit exists and is unreachable is better served than
          one shown nothing; a site's owner reading `gallery.image_added ·
          place` is being shown the wire. */}
      {register === 'build' && structural.length > 0 && (
        <IntentChips
          heading="Structural intents"
          intents={structural}
          roles={roles}
          note="These name a placement, so they belong to the tree controls rather than to a form — a text box for a
                link id can only produce a command the fold refuses, or one it accepts against the wrong placement.
                What each one does to the tree is read off its payload; what it means is read off its name."
        />
      )}

      {register === 'build' && undrawable.length > 0 && (
        <IntentChips
          heading="No control yet"
          intents={undrawable}
          note="The surface honours these and this client has nothing to send them with — each one edits a list, and
                arranging a list means knowing what its entries are, which is more than a schema says."
        />
      )}
    </Stack>
  )
}

/**
 * A group of intents this editor offers no control for, named rather
 * than hidden.
 *
 * Read off the manifest, so it is what the surface will actually honour
 * — which is the point of showing it at all. An author who can see that
 * an edit exists and is unreachable is better served than one shown
 * nothing.
 */
function IntentChips({
  heading,
  intents,
  roles,
  note,
}: {
  heading: string
  intents: ManifestIntent[]
  roles?: Map<string, StructuralRole>
  note: string
}) {
  return (
    <Stack spacing={0.5}>
      <Divider />
      <Typography variant="h3" sx={{ pt: 1 }}>
        {heading}
      </Typography>
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {intents.map((intent) => {
          const role = roles?.get(intent.name)
          return (
            <Chip
              key={intent.name}
              size="small"
              variant="outlined"
              label={role ? `${intent.name} · ${role}` : intent.name}
            />
          )
        })}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {note}
      </Typography>
    </Stack>
  )
}
